import React from 'react';
import CheckIcon from 'src/assets/icons/check.svg';
import {CommonSpinLoader} from '../../../ui-components/loaders';
import {BillingCycle} from '../../../utils/tier';

export interface PixelPlanProps {
    title: string;
    titleGradient: string;
    subtitle?: string;
    price?: string;
    description?: string;
    benefits: Benefit[];
    ctaText: string;
    currentPlan: boolean;
    onPlanSelect: () => void;
    loading: boolean;
    horizontalCard: boolean | false;
    expiry?: number;
    billingCycle: BillingCycle;
    infoText?: string;
}

interface Benefit {
    text: string;
    subBenefits?: Benefit[];
}

function PixelPlanButton(props: {children: React.ReactNode; disabled: boolean; onClick: () => void}) {
    return (
        <div className={'flex w-full flex-col items-center rounded-bl-2xl rounded-br-2xl py-6 lg:py-8'}>
            <div
                onClick={props.onClick}
                className={`items-center self-stretch rounded-lg px-5 py-3 text-center ${
                    props.disabled ? 'bg-gray-200' : 'bg-[#155EEF]'
                }
                hover:${!props.disabled ? 'bg-[#0040C1]' : null} font-inter font-semibold focus:bg-[#155EEF] 
                ${props.disabled ? 'text-gray-950' : 'text-white'} cursor-pointer`}>
                {props.children}
            </div>
        </div>
    );
}

function LineItem({benefit}: {benefit: Benefit}) {
    return (
        <li className={'font-inter flex py-1 align-middle text-[14px] text-gray-600'}>
            <img src={CheckIcon} alt={'check'} className={'mr-3 h-6 w-6'} />
            {benefit.text}
            {benefit.subBenefits && (
                <ul className=''>
                    {benefit.subBenefits.map((subBenefit, index) => (
                        <LineItem key={index} benefit={subBenefit} />
                    ))}
                </ul>
            )}
        </li>
    );
}

function BenefitItem({benefit}: {benefit: Benefit}) {
    return (
        <li className='font-inter flex flex-col py-1 text-gray-500'>
            <div className='flex items-start'>
                <img src={CheckIcon} alt={'check'} className={'mr-3 h-6 w-6'} />
                <span>{benefit.text}</span>
            </div>
            {benefit.subBenefits && (
                <ul className='list-none pl-3'>
                    {benefit.subBenefits.map((subBenefit, index) => (
                        <BenefitItem key={index} benefit={subBenefit} />
                    ))}
                </ul>
            )}
        </li>
    );
}

export const PixelPlan: React.FC<PixelPlanProps> = ({
    title,
    subtitle,
    price,
    description,
    benefits,
    ctaText,
    currentPlan,
    titleGradient,
    onPlanSelect,
    loading,
    horizontalCard,
    expiry,
    billingCycle,
    infoText,
}) => {
    const mid = horizontalCard ? Math.ceil(benefits.length / 2) : benefits.length;
    const firstHalfBenefits = benefits.slice(0, mid);
    const remainingBenefits = benefits.slice(mid);

    return (
        <>
            <div
                className={`rounded-2xl border-2 ${currentPlan ? 'border-blue-600' : 'border-gray-200'} 
            flex flex-col bg-white ${horizontalCard ? 'lg:grid lg:grid-cols-3 lg:gap-20' : null} w-full justify-start`}>
                <div className={`flex flex-col items-start px-6 align-middle ${!horizontalCard ? 'pt-8' : 'pt-4'}`}>
                    {/*PLAN NAME*/}
                    <div
                        className={`font-inter bg-gradient-to-r text-[28px] font-extrabold ${titleGradient} bg-clip-text text-transparent`}>
                        {title}
                    </div>
                    <div className={'font-inter text-[14px] text-sm text-gray-500'}>{subtitle ? subtitle : ''}</div>

                    {/*PRICE*/}
                    {price && (
                        <div className='w-216 h-38 mt-4 flex flex-shrink-0 items-center justify-center gap-2'>
                            <div className='font-inter text-[28px] font-extrabold text-black'>{price}</div>
                            <div className='font-inter text-base font-normal text-[#A2A2A2]'>
                                {billingCycle === BillingCycle.Monthly ? '/ month' : '/ year'}
                            </div>
                        </div>
                    )}

                    {/*DESc*/}
                    {description && (
                        <div className='w-216 h-38 mt-8 flex flex-shrink-0 items-center justify-center gap-2'>
                            <div className='font-inter text-[19px] font-extrabold text-black'>{description}</div>
                        </div>
                    )}

                    {/*CTA*/}
                    {horizontalCard ? (
                        <button
                            onClick={onPlanSelect}
                            className={'text-inter pt-4 text-[14px] font-semibold text-blue-600'}>
                            {ctaText}
                        </button>
                    ) : (
                        <PixelPlanButton onClick={onPlanSelect} disabled={currentPlan || loading}>
                            {loading ? (
                                <div className='flex items-center justify-center'>
                                    <CommonSpinLoader size={'sm'} />
                                </div>
                            ) : (
                                ctaText
                            )}
                        </PixelPlanButton>
                    )}
                </div>

                {/*DIVIDER*/}
                {!horizontalCard ? <div className='mx-6 rounded-lg border-b border-solid border-gray-200'></div> : null}

                {/*INFO TEXT*/}
                {infoText && (
                    <div className={`mt-4 flex flex-col px-6 lg:px-8`}>
                        <p className='font-inter text-md font-bold text-black'>{infoText}</p>
                    </div>
                )}

                {/*BENEFITS*/}
                <div className='flex flex-col p-6 lg:py-4'>
                    <ul className='list-none'>
                        {firstHalfBenefits.map((item, index) => (
                            <div key={index}>
                                <BenefitItem benefit={item} />
                            </div>
                        ))}
                    </ul>
                </div>
                {remainingBenefits.length > 0 && (
                    <div className='flex flex-col p-6 lg:py-4'>
                        <ul className='list-none'>
                            {remainingBenefits.map((item, index) => (
                                <div key={index}>
                                    <BenefitItem benefit={item} />
                                </div>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </>
    );
};
